import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { LowStockDto } from '@ecom/contracts';
import { AsyncSelect } from '@/components/async-select';
import { categoryOptions } from '@/lib/options';
import { FilterBar, FilterField } from '@/components/filters';
import { useInventoryStore } from '@/stores/inventory.store';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { count, dateTime } from '@/lib/format';
import { labelOr } from '@/lib/labels';
import {
  LOW_STOCK_LIMIT,
  LOW_STOCK_THRESHOLDS,
  RECOUNT_REASONS,
  parseCount,
  signedCount,
  type RecountReason,
} from '@/lib/stock';
import { BoxIcon, CheckIcon } from '@/components/icons';
import { ListCard, ListToolbar, LoadMore, Page, Sheet, Table, TableScroll, Tabs, Td, Th, Tr } from '@/components/page';
import {
  Alert,
  Button,
  CardHeader,
  EmptyState,
  Field,
  Input,
  Pill,
  Select,
  Skeleton,
  TableSkeleton,
  Textarea,
  cx,
} from '@/components/ui';

/** The tab that sends no threshold, so the API applies the store's own. */
const STORE_SETTING = 'store';

/** A movement's direction, at a glance; the signed figure beside it says how much. */
const TYPE_TONE = { IN: 'success', OUT: 'neutral', ADJUST: 'warning' } as const;

/**
 * Inventory.
 *
 * Two halves, side by side and each the height of the window: what is running
 * out, and why a given level is what it is. The movement history is the second
 * half — an unexplained stock figure is a figure nobody trusts. A recount opens
 * in a side panel, over the list it came from, and asks why the number changed.
 */
export function InventoryPage() {
  const {
    lowStock,
    threshold,
    search,
    categoryId,
    loading,
    error,
    movementsOf,
    movements,
    movementsCursor,
    movementsLoading,
    movementsLoadingMore,
    movementsError,
    locations,
    load,
    loadMovements,
    loadMoreMovements,
    loadLocations,
  } = useInventoryStore();

  const locale = useUiStore((state) => state.locale);
  const t = useT();

  const [counting, setCounting] = useState<LowStockDto | null>(null);
  const [moving, setMoving] = useState<LowStockDto | null>(null);

  // Where the open variant's stock is (ADR-089): shown only when the store keeps it in more than one place.
  const places = movementsOf && locations?.variantId === movementsOf.variantId && locations.locations.length > 1 ? locations : null;
  const placeName = (id: string | null) => {
    const place = id ? places?.locations.find((entry) => entry.locationId === id) : undefined;
    return place ? place.name[locale] || place.name.en : null;
  };
  const recountOf = (item: LowStockDto) => {
    if (locations?.variantId !== item.variantId) void loadLocations(item.variantId);
    setCounting(item);
  };

  // The picker returns the option it chose, so the chip names the category
  // without this page holding every category to look one up.
  const [categoryLabel, setCategoryLabel] = useState('');

  useEffect(() => {
    void load();
  }, [load, locale]);

  /** A reason's words; a slug the API adds tomorrow still shows, as itself. */
  const reasonLabel = (reason: string) => labelOr(t, `movementReason_${reason}`, reason);

  // The API returns at most this many, lowest stock first, and no next page —
  // so a full list says that it is the first of more, not all there is.
  const capped = lowStock.length >= LOW_STOCK_LIMIT;

  return (
    <Page title={t('inventory')} subtitle={t('inventorySubtitle')} fill>
      <div className="grid flex-1 gap-5 lg:min-h-0 xl:grid-cols-[1.3fr_1fr]">
        <ListCard>
          <CardHeader title={t('lowStock')} subtitle={t('lowStockHint')} />
          <Tabs
            className="px-2"
            label={t('threshold')}
            value={threshold === null ? STORE_SETTING : String(threshold)}
            onChange={(value) => void load({ threshold: value === STORE_SETTING ? null : Number(value) })}
            items={[
              { id: STORE_SETTING, label: t('thresholdStoreSetting') },
              ...LOW_STOCK_THRESHOLDS.map((value) => ({
                id: String(value),
                label: t('stockAtMost', { count: count(value, locale) }),
              })),
            ]}
          />

          <ListToolbar>
            <FilterBar
              search={search}
              onSearch={(next) => void load({ search: next })}
              placeholder={t('searchInventoryHint')}
              chips={
                categoryId
                  ? [
                      {
                        key: 'category',
                        label: categoryLabel || t('category'),
                        onRemove: () => {
                          setCategoryLabel('');
                          void load({ categoryId: '' });
                        },
                      },
                    ]
                  : []
              }
              onClearAll={() => {
                setCategoryLabel('');
                void load({ search: '', categoryId: '' });
              }}
            >
              <FilterField label={t('category')} htmlFor="inv-category">
                <AsyncSelect
                  id="inv-category"
                  value={categoryId}
                  emptyLabel={t('allCategories')}
                  fetchPage={categoryOptions.page}
                  fetchOne={categoryOptions.one}
                  onChange={(id, option) => {
                    setCategoryLabel(option?.label ?? '');
                    void load({ categoryId: id });
                  }}
                />
              </FilterField>
            </FilterBar>

            {lowStock.length > 0 && !loading ? (
              <p className="text-xs text-ink-muted">
                {t(capped ? 'lowStockCapped' : 'lowStockShown', { shown: count(lowStock.length, locale) })}
              </p>
            ) : null}
          </ListToolbar>

          {error ? (
            <div className="px-4 pt-4">
              <Alert>{error}</Alert>
            </div>
          ) : null}

          {loading && lowStock.length === 0 ? (
            <TableSkeleton rows={5} columns={3} />
          ) : lowStock.length === 0 ? (
            <EmptyState title={t('noLowStock')} icon={<CheckIcon />} />
          ) : (
            // A refresh keeps the rows, faded, rather than flashing a skeleton
            // after every recount.
            <TableScroll className={cx('transition-opacity', loading && 'opacity-60')}>
              <Table>
                <thead>
                  <tr>
                    <Th>{t('product')}</Th>
                    <Th>{t('stock')}</Th>
                    <Th align="end" />
                  </tr>
                </thead>
                <tbody aria-busy={loading}>
                  {lowStock.map((item) => (
                    // The row opens its history, as the button does.
                    <Tr
                      key={item.variantId}
                      onClick={() => void loadMovements(item)}
                      className={cx(item.variantId === movementsOf?.variantId && 'bg-surface-sunken')}
                    >
                      <Td>
                        {/* The product, to restock or reprice it; the row itself opens the history. */}
                        <Link
                          to={`/products/${item.productId}`}
                          onClick={(event) => event.stopPropagation()}
                          className="block text-ink hover:underline"
                        >
                          {item.productName}
                        </Link>
                        <span dir="ltr" data-numeric className="block text-xs text-ink-muted">
                          {item.sku}
                        </span>
                      </Td>
                      <Td>
                        <Pill tone={item.stock <= 0 ? 'danger' : 'warning'}>
                          {/* Below zero is what pre-orders are owed (ADR-079); a restock fills it first. */}
                          {item.stock < 0
                            ? t('owedUnits', { count: count(-item.stock, locale) })
                            : item.stock === 0
                              ? t('outOfStock')
                              : `${count(item.stock, locale)} ${t('unit')}`}
                        </Pill>
                      </Td>
                      <Td align="end">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(event) => {
                              event.stopPropagation();
                              void loadMovements(item);
                            }}
                          >
                            {t('movements')}
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={(event) => {
                              event.stopPropagation();
                              recountOf(item);
                            }}
                          >
                            {t('recount')}
                          </Button>
                        </div>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableScroll>
          )}
        </ListCard>

        <ListCard>
          <CardHeader
            title={t('movements')}
            subtitle={movementsOf?.productName ?? t('movementsHint')}
            action={
              movementsOf ? (
                <div className="flex gap-2">
                  {places ? (
                    <Button variant="ghost" size="sm" onClick={() => setMoving(movementsOf)}>
                      {t('transferStock')}
                    </Button>
                  ) : null}
                  <Button variant="secondary" size="sm" onClick={() => recountOf(movementsOf)}>
                    {t('recount')}
                  </Button>
                </div>
              ) : null
            }
          />
          {movementsOf ? (
            <div className="flex flex-wrap items-center justify-between gap-2 px-5 pb-3 text-xs text-ink-muted">
              <span dir="ltr" data-numeric>
                {movementsOf.sku}
              </span>
              <span>{t('recountOnHand', { count: count(places?.total ?? movementsOf.stock, locale) })}</span>
            </div>
          ) : null}
          {places ? (
            <ul className="mx-5 mb-3 flex flex-col divide-y divide-line rounded-control border border-line text-sm">
              {places.locations.map((place) => (
                <li key={place.locationId} className="flex items-center justify-between gap-3 px-3 py-2">
                  <span className={cx('text-ink', !place.isActive && 'text-ink-muted')}>
                    {place.name[locale] || place.name.en}
                    {place.isDefault ? <span className="ms-2 text-xs text-ink-muted">{t('locationMain')}</span> : null}
                  </span>
                  <bdi data-numeric className={cx('tabular-nums', place.quantity < 0 ? 'text-danger' : 'text-ink')}>
                    {count(place.quantity, locale)}
                  </bdi>
                </li>
              ))}
            </ul>
          ) : null}

          {movementsError ? (
            <div className="px-4 pb-3">
              <Alert>{movementsError}</Alert>
            </div>
          ) : null}

          {!movementsOf ? (
            <EmptyState title={t('movementsPick')} icon={<BoxIcon />} />
          ) : movementsLoading ? (
            <div className="space-y-3 p-5">
              {Array.from({ length: 5 }, (_, i) => (
                <Skeleton key={i} className="h-10" />
              ))}
            </div>
          ) : movements.length === 0 ? (
            movementsError ? null : <EmptyState title={t('noMovements')} icon={<BoxIcon />} />
          ) : (
            <TableScroll>
              <ul className="flex flex-col">
                {movements.map((movement) => (
                  <li
                    key={movement.id}
                    className="flex items-start justify-between gap-3 border-t border-line px-5 py-3 text-sm"
                  >
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-ink">{reasonLabel(movement.reason)}</span>
                        <Pill tone={TYPE_TONE[movement.type]}>
                          {t(`movementType${movement.type}`)}
                        </Pill>
                      </div>
                      <span className="mt-0.5 block text-xs text-ink-muted">
                        {dateTime(movement.createdAt, locale)}
                        {movement.actorName ? ` · ${movement.actorName}` : ''}
                        {placeName(movement.locationId) ? ` · ${placeName(movement.locationId)}` : ''}
                        {movement.orderId && movement.orderNumber ? (
                          <>
                            {' · '}
                            <Link to={`/orders/${movement.orderId}`} dir="ltr" data-numeric className="text-brand-700 hover:underline">
                              {movement.orderNumber}
                            </Link>
                          </>
                        ) : null}
                      </span>
                      {movement.note ? (
                        <p className="mt-1 text-xs break-words text-ink-muted">{movement.note}</p>
                      ) : null}
                    </div>
                    {/* Signed: a recount downwards must never read as a delivery. */}
                    <bdi
                      data-numeric
                      className={cx(
                        'shrink-0 font-medium tabular-nums',
                        movement.quantity < 0 ? 'text-danger' : 'text-success',
                      )}
                    >
                      {signedCount(movement.quantity, locale)}
                    </bdi>
                  </li>
                ))}
              </ul>
              <LoadMore
                visible={Boolean(movementsCursor)}
                loading={movementsLoadingMore}
                onClick={() => void loadMoreMovements()}
              />
            </TableScroll>
          )}
        </ListCard>
      </div>

      <RecountSheet item={counting} onClose={() => setCounting(null)} />
      <TransferSheet item={moving} onClose={() => setMoving(null)} />
    </Page>
  );
}

/**
 * The recount panel: the figure on the shelf, and why it is not the one on
 * record. The difference is shown before saving, worded as the movement it
 * will become — or as nothing, when there is none to record.
 */
function RecountSheet({ item, onClose }: { item: LowStockDto | null; onClose: () => void }) {
  const { saving, saveError, recount, clearSaveError, locations } = useInventoryStore();
  const locale = useUiStore((state) => state.locale);
  const t = useT();

  const [countedStock, setCountedStock] = useState('');
  const [reason, setReason] = useState<RecountReason>('recount');
  const [note, setNote] = useState('');
  /** Where the shelf was counted (ADR-089); empty: the whole store's total. */
  const [locationId, setLocationId] = useState('');

  const places = item && locations?.variantId === item.variantId && locations.locations.length > 1 ? locations.locations : null;
  const place = places?.find((entry) => entry.locationId === locationId) ?? null;
  const onRecord = place ? place.quantity : item ? (locations?.variantId === item.variantId ? locations.total : item.stock) : 0;

  // Each opening starts from the figure on record, as a plain recount of the whole store.
  useEffect(() => {
    if (!item) return;
    setCountedStock(String(item.stock));
    setReason('recount');
    setNote('');
    setLocationId('');
    clearSaveError();
  }, [item, clearSaveError]);

  const counted = parseCount(countedStock);
  const delta = item && counted !== null ? counted - onRecord : null;

  async function submit() {
    if (!item || counted === null) return;
    const ok = await recount(item, { stock: counted, reason, note: note.trim() || undefined, locationId: locationId || undefined });
    if (ok) onClose();
  }

  return (
    <Sheet
      open={item !== null}
      title={t('recountTitle')}
      subtitle={t('recountBody')}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            {t('cancel')}
          </Button>
          <Button disabled={saving || counted === null} onClick={() => void submit()}>
            {saving ? t('saving') : t('save')}
          </Button>
        </>
      }
    >
      {item ? (
        <form
          className="flex flex-col gap-5"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          <div className="rounded-control border border-line bg-surface-sunken px-3 py-2.5">
            <p className="text-sm font-medium text-ink">{item.productName}</p>
            <p dir="ltr" data-numeric className="text-xs text-ink-muted">
              {item.sku}
            </p>
            <p className="mt-1 text-xs text-ink-muted">{t('recountOnHand', { count: count(onRecord, locale) })}</p>
          </div>

          {places ? (
            <Field label={t('recountWhere')} htmlFor="recount-location" hint={locationId ? undefined : t('recountWhereTotalHint')}>
              <Select
                id="recount-location"
                className="w-full"
                value={locationId}
                onChange={(event) => {
                  const next = places.find((entry) => entry.locationId === event.target.value);
                  setLocationId(event.target.value);
                  setCountedStock(String(next ? next.quantity : (locations?.total ?? item.stock)));
                }}
              >
                <option value="">{t('recountWhereTotal')}</option>
                {places.map((entry) => (
                  <option key={entry.locationId} value={entry.locationId}>
                    {entry.name[locale] || entry.name.en}
                  </option>
                ))}
              </Select>
            </Field>
          ) : null}

          <Field
            label={t('countedStock')}
            htmlFor="countedStock"
            hint={
              delta === null
                ? undefined
                : delta === 0
                  ? t('recountNoChange')
                  : t('recountDifference', { delta: signedCount(delta, locale) })
            }
          >
            {/* A quantity someone types stays Latin on screen — it is data, not prose. */}
            <Input
              id="countedStock"
              dir="ltr"
              inputMode="numeric"
              value={countedStock}
              invalid={countedStock.trim() !== '' && counted === null}
              onChange={(event) => setCountedStock(event.target.value)}
            />
          </Field>

          <fieldset className="flex flex-col gap-2">
            <legend className="mb-2 text-sm font-medium text-ink">{t('recountReason')}</legend>
            {RECOUNT_REASONS.map((option) => (
              <label
                key={option}
                className={cx(
                  'flex cursor-pointer items-start gap-3 rounded-control border px-3 py-2.5 transition-colors',
                  reason === option
                    ? 'border-brand-500 bg-surface-sunken ring-1 ring-brand-500'
                    : 'border-line hover:bg-surface-sunken',
                )}
              >
                <input
                  type="radio"
                  name="recount-reason"
                  value={option}
                  checked={reason === option}
                  onChange={() => setReason(option)}
                  className="mt-0.5 size-4 accent-brand-600"
                />
                <span className="flex flex-col">
                  <span className="text-sm text-ink">{t(`movementReason_${option}`)}</span>
                  <span className="text-xs text-ink-muted">{t(`recountReasonHint_${option}`)}</span>
                </span>
              </label>
            ))}
          </fieldset>

          <Field label={t('note')} htmlFor="recount-note" hint={t('optional')}>
            <Textarea
              id="recount-note"
              rows={2}
              maxLength={300}
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          </Field>

          {saveError ? <Alert>{saveError}</Alert> : null}
        </form>
      ) : null}
    </Sheet>
  );
}

/**
 * Moving stock between locations (ADR-089): out of one, into the other, on
 * the ledger at both. The store's total does not change.
 */
function TransferSheet({ item, onClose }: { item: LowStockDto | null; onClose: () => void }) {
  const { saving, saveError, transfer, clearSaveError, locations } = useInventoryStore();
  const locale = useUiStore((state) => state.locale);
  const t = useT();
  const places = item && locations?.variantId === item.variantId ? locations.locations : [];

  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [note, setNote] = useState('');

  // Each opening: from the main location, to the first other one.
  useEffect(() => {
    if (!item) return;
    const main = places.find((entry) => entry.isDefault);
    setFrom(main?.locationId ?? '');
    setTo(places.find((entry) => !entry.isDefault && entry.isActive)?.locationId ?? '');
    setQuantity('1');
    setNote('');
    clearSaveError();
    // `places` follows `item`; re-running on every read would reset the form under the person typing.
  }, [item, clearSaveError]);

  const source = places.find((entry) => entry.locationId === from);
  const amount = parseCount(quantity);
  const ready = Boolean(from && to && from !== to && amount !== null && amount > 0 && source && amount <= source.quantity);
  const label = (entry: (typeof places)[number]) =>
    `${entry.name[locale] || entry.name.en} — ${count(entry.quantity, locale)}`;

  async function submit() {
    if (!item || !ready || amount === null) return;
    const ok = await transfer(item, { fromLocationId: from, toLocationId: to, quantity: amount, note: note.trim() || undefined });
    if (ok) onClose();
  }

  return (
    <Sheet
      open={item !== null}
      title={t('transferStock')}
      subtitle={t('transferStockBody')}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            {t('cancel')}
          </Button>
          <Button disabled={saving || !ready} onClick={() => void submit()}>
            {saving ? t('saving') : t('transferStockSubmit')}
          </Button>
        </>
      }
    >
      {item ? (
        <form
          className="flex flex-col gap-5"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          <div className="rounded-control border border-line bg-surface-sunken px-3 py-2.5">
            <p className="text-sm font-medium text-ink">{item.productName}</p>
            <p dir="ltr" data-numeric className="text-xs text-ink-muted">
              {item.sku}
            </p>
          </div>
          <Field label={t('transferFrom')} htmlFor="transfer-from">
            <Select id="transfer-from" className="w-full" value={from} onChange={(event) => setFrom(event.target.value)}>
              {places.map((entry) => (
                <option key={entry.locationId} value={entry.locationId}>
                  {label(entry)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('transferTo')} htmlFor="transfer-to">
            <Select id="transfer-to" className="w-full" value={to} onChange={(event) => setTo(event.target.value)}>
              <option value="" disabled>
                {t('choose')}
              </option>
              {places
                .filter((entry) => entry.locationId !== from)
                .map((entry) => (
                  <option key={entry.locationId} value={entry.locationId}>
                    {label(entry)}
                  </option>
                ))}
            </Select>
          </Field>
          <Field
            label={t('quantity')}
            htmlFor="transfer-quantity"
            hint={source ? t('transferAvailable', { count: count(Math.max(0, source.quantity), locale) }) : undefined}
          >
            <Input
              id="transfer-quantity"
              dir="ltr"
              inputMode="numeric"
              value={quantity}
              invalid={quantity.trim() !== '' && (amount === null || (source !== undefined && amount > source.quantity))}
              onChange={(event) => setQuantity(event.target.value)}
            />
          </Field>
          <Field label={t('note')} htmlFor="transfer-note" hint={t('optional')}>
            <Textarea id="transfer-note" rows={2} maxLength={300} value={note} onChange={(event) => setNote(event.target.value)} />
          </Field>
          {saveError ? <Alert>{saveError}</Alert> : null}
        </form>
      ) : null}
    </Sheet>
  );
}
